import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import ButtonLabeled from "./ButtonLabeled";
import DatePopUp from "./DatePopUp";
import DatePopUpV2 from "./DatePopUpV2";
import List from "./List";
import moment from "moment/moment";
import CustomersPopUp from "./CustomersPopUp";
import Table from "../icons/Table";
import Takeaway from "../icons/Takeaway";
import Delivery from "../icons/Delivery";
import ListIcon from "../icons/ListIcon";
import UserIcon from "../icons/UserIcon";
import CalendarIcon from "../icons/CalendarIcon";
import BookingTableIcon from "../icons/BookingTableIcon";
import BarIcon from "../icons/BarIcon";
import HamburgerIcon from "../icons/HamburgerIcon";
import LightModeLockedPopup from "./LightModeLockedPopup";

function sortByBackendPosition(items = []) {
    return [...items]
        .map((item, index) => ({ item, index }))
        .sort((a, b) => {
            const isAOther =
                Number(a.item?.is_flat_out_of_course) === 1 ||
                (Number(a.item?.id) === -1 && String(a.item?.name || "").trim() === "");
            const isBOther =
                Number(b.item?.is_flat_out_of_course) === 1 ||
                (Number(b.item?.id) === -1 && String(b.item?.name || "").trim() === "");

            if (isAOther && !isBOther) return 1;
            if (!isAOther && isBOther) return -1;

            const aPosition = Number(a.item?.position);
            const bPosition = Number(b.item?.position);
            const hasAPosition = Number.isFinite(aPosition);
            const hasBPosition = Number.isFinite(bPosition);

            if (hasAPosition && hasBPosition && aPosition !== bPosition) return aPosition - bPosition;
            if (hasAPosition && !hasBPosition) return -1;
            if (!hasAPosition && hasBPosition) return 1;
            return a.index - b.index;
        })
        .map(({ item }) => item);
}

function Header() {
    const {
        apiSettings,
        cart,
        setPopup,
        lists,
        editCart2,
        setFilteredCategories,
        menu,
        discountsProducts,
        discountsAvail,
        category,
        setCategory,
        shop,
        list,
        setList,
        setSearchTerm,
        editTable,
        setTablesListVisible,
        stock,
        colors,
        lockEdit,
        setSearchAddressTerm,
        setActiveFlow,
        toggleSubmenu,
        lightMode,
        config,
    } = useContext(AppContext);
    const [filteredLists, setFilteredLists] = useState([]);
    const [reservationsPopupOpen, setReservationsPopupOpen] = useState(false);
    const hasCustomerData = Boolean(
        cart?.customer || ((cart?.name || "").trim() && (cart?.surname || "").trim()),
    );

    const getReservationsClientUrl = () => {
        const backofficeUrl = apiSettings?.global?.url_backoffice;
        if (!backofficeUrl) return null;

        const token = localStorage.getItem("pos-user") || "";
        const lang = (navigator.language || "it").split("-")[0];

        try {
            const normalizedBase = backofficeUrl.endsWith("/") ? backofficeUrl : `${backofficeUrl}/`;
            const url = new URL("bookings/", normalizedBase);
            if (token) url.searchParams.set("token", token);
            url.searchParams.set("lang", lang);
            return url.toString();
        } catch (error) {
            console.error("Invalid reservations client url", error);
            return null;
        }
    };

    const openLightModeLockedPopup = () => {
        setPopup({
            hideHeader: true,
            customClass: "lightmode-locked-popup",
            content: <LightModeLockedPopup />,
        });
    };

    const openDatePopup = () => {
        setPopup({
            title: "Data",
            content:
                config?.time_selection_v2 == 1 ? (
                    <DatePopUpV2 />
                ) : (
                    <DatePopUp />
                ),
            size: config?.time_selection_v2 == 1 ? "large" : "",
            customClass: config?.time_selection_v2 == 1 ? "dp2-fullscreen" : "",
            hideHeader: config?.time_selection_v2 == 1,
        });
    };

    const openReservationsClient = () => {
        const iframeUrl = getReservationsClientUrl();

        if (!iframeUrl) {
            setPopup({
                title: "Prenotazioni",
                content: (
                    <div className="external-reservations-error">
                        URL client prenotazioni non configurato.
                    </div>
                ),
            });
            return;
        }

        setPopup({
            title: "Prenotazioni",
            onClose: false,
            hideHeader: true,
            customClass: "external-reservations-popup",
            fullContent: (
                <div className="external-reservations-iframe-wrap">
                    <iframe title="Prenotazioni" src={iframeUrl} className="external-reservations-iframe" />
                </div>
            ),
        });
        setReservationsPopupOpen(true);
    };

    useEffect(() => {
        const onMessage = (event) => {
            const data = event?.data;
            const shouldClose =
                data === "close" ||
                data === "bookings:close" ||
                data?.type === "close" ||
                data?.type === "bookings:close" ||
                data?.event === "close" ||
                data?.event === "bookings:close";

            if (reservationsPopupOpen && shouldClose) {
                setPopup(false);
                setReservationsPopupOpen(false);
            }
        };

        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
    }, [reservationsPopupOpen, setPopup]);

    useEffect(() => {
        if (shop) filterLists();
    }, [shop, lists]);

    useEffect(() => {
        if (list) localStorage.setItem("pos-list", JSON.stringify(list));
        else localStorage.removeItem("pos-list");
    }, [list]);

    useEffect(() => {
        if (shop && !list && apiSettings.pos.hide_base_list == 1) {
            setPopup({
                title: "Listini",
                content: (
                    <List
                        mode={3}
                        elements={filteredLists}
                        onClick={(l) => {
                            setPopup(false);
                            setList(l);
                            editCart2({
                                list_id: l.id,
                            });
                        }}
                        cancelElement={apiSettings.pos.hide_base_list == 1 ? false : "Listino Base"}
                    />
                ),
            });
        }
    }, [shop, list, filteredLists]);

    // pezzi gia' nel carrello per ogni sconto-prodotto: non sono piu' disponibili per nuove righe scontate.
    // La chiave evita di ricalcolare tutto il menu a ogni modifica del carrello: cambia solo se cambiano questi conteggi.
    const discountQtyInCart = {};
    for (const p of cart?.products || []) {
        if (p.discounts_products_id) {
            discountQtyInCart[p.discounts_products_id] =
                (discountQtyInCart[p.discounts_products_id] || 0) + parseFloat(p.qty || 0);
        }
    }
    const discountQtyInCartKey = JSON.stringify(discountQtyInCart);

    useEffect(() => {
        if (menu) filterMenu();
    }, [menu, list, shop, apiSettings, stock, colors, discountsProducts, discountsAvail, discountQtyInCartKey]);

    const filterLists = () => {
        //console.log("filterLists");
        const sourceLists = Array.isArray(lists) ? lists : [];
        const filteredArr = sourceLists.filter((el) => {
            return (
                (el.shops || []).filter((s) => {
                    return s.id == shop.id;
                }).length > 0
            );
        });
        setFilteredLists(filteredArr);
    };

    // Sconti-prodotto (stessa logica del kiosk): negozio, listino, date e quantita' residua.
    // Gli sconti one_per_customer sono esclusi (richiedono il cliente, non gestito dal POS).
    function applyProductDiscount(p) {
        const today = moment().startOf("day");
        const rule = (discountsProducts || []).find((discount) => {
            if (discount.product_id != p.id) return false;
            if (discount.one_per_customer == 1) return false;
            if (discount.shops_ids?.length && !discount.shops_ids.some((id) => id == shop.id)) return false;
            if (discount.lists_ids?.length && !discount.lists_ids.some((id) => id == list?.id)) return false;
            if (discount.date_start && today < moment(discount.date_start)) return false;
            if (discount.date_end && today > moment(discount.date_end)) return false;
            if (discount.has_qty_limit == 1) {
                const qty = discountsAvail?.[discount.id];
                if (qty === undefined || qty === null || qty - (discountQtyInCart[discount.id] || 0) <= 0) return false;
            }
            return true;
        });
        if (!rule) return;
        const original = Number(p.price);
        const value = Number(rule.value);
        const discounted = rule.type == "perc" ? original - (original / 100) * value : original - value;
        p.original_price = p.price;
        p.price = Math.max(0, Math.round(discounted * 100) / 100);
        // importo dello sconto per pezzo: serve a ripristinare il prezzo pieno anche quando alla riga sono stati aggiunti extra (varianti)
        p.discount_amount = Math.round((original - p.price) * 100) / 100;
        p.discounted = true;
        p.discounts_products_id = rule.id;
        // pezzi ancora scontabili (solo per gli sconti a quantita' limitata), mostrati sulla scheda prodotto
        if (rule.has_qty_limit == 1) p.discount_remaining = Number(discountsAvail?.[rule.id]) - (discountQtyInCart[rule.id] || 0);
    }

    function filterMenu() {
        console.log("filterMenu");
        let res = [];
        for (const course of sortByBackendPosition(menu || [])) {
            const cr = structuredClone(course);
            cr.categories = [];
            for (const category of sortByBackendPosition(course.categories || [])) {
                if (!category.hasOwnProperty("active_pos") || category.active_pos == 1) {
                    const ct = structuredClone(category);
                    if (shop && list) {
                        ct.products = [];
                        for (const product of category.products || []) {
                            let p = filterVariants(product);
                            p.stock = getProductStock(p.id);
                            p.color = getProductColor(p.id);
                            p.category_name = category.name;
                            const priceList = product.prices.filter((el) => {
                                return el.list_id == list.id && el.price;
                            });
                            const assortment = product.assortment.filter((el) => {
                                return el.shop_id == shop.id && el.list_id == list.id && el.active == 1;
                            });
                            if (
                                priceList.length > 0 &&
                                assortment.length > 0 &&
                                check_ingredients_assortment(product)
                            ) {
                                p.price = priceList[0].price;
                                applyProductDiscount(p);
                                ct.products.push(p);
                            }
                        }
                        if (ct.products.length > 0) cr.categories.push(ct);
                    } else if (shop) {
                        ct.products = [];
                        for (const product of category.products || []) {
                            let p = filterVariants(product);
                            p.stock = getProductStock(p.id);
                            p.color = getProductColor(p.id);
                            p.category_name = category.name;
                            if (check_ingredients_assortment(product)) {
                                applyProductDiscount(p);
                                ct.products.push(p);
                            }
                        }
                        if (ct.products.length > 0) cr.categories.push(ct);
                    } else {
                        if ((category.products || []).length > 0) {
                            const sortedCategory = structuredClone(category);
                            sortedCategory.products = (category.products || []).map((product) => ({
                                ...product,
                                category_name: category.name,
                            }));
                            cr.categories.push(sortedCategory);
                        }
                    }
                }
            }
            cr.categories = sortByBackendPosition(cr.categories);
            if (cr.categories.length > 0) res.push(cr);
        }
        res = sortByBackendPosition(res);
        setFilteredCategories(res);
        if (!category && res.length > 0 && res[0].categories.length > 0) setCategory(res[0].categories[0]);
        if (category) {
            const i = res.findIndex((vc) => {
                const cat = vc.categories.filter((c) => {
                    return c.id == category.id;
                });
                return cat.length > 0;
            });

            if (i > -1) {
                const n = res[i].categories.findIndex((c) => c.id == category.id);
                if (n > -1) setCategory(res[i].categories[n]);
                else setCategory(res[0].categories[0]);
            } else if (res.length == 0) setCategory({ categories: [] });
            else setCategory(res[0].categories[0]);
        }
    }

    const filterVariants = (product) => {
        const normalizeOrigins = (originsRaw) => {
            if (Array.isArray(originsRaw))
                return originsRaw.map((origin) => String(origin || "").trim().toLowerCase()).filter(Boolean);
            if (typeof originsRaw === "string")
                return originsRaw
                    .split(",")
                    .map((origin) => String(origin || "").trim().toLowerCase())
                    .filter(Boolean);
            return [];
        };

        const isVariantCategoryEnabledForPos = (variantsCategory) => {
            const origins = normalizeOrigins(variantsCategory?.origins);
            if (origins.length === 0) return true;
            if (origins.includes("pos")) return true;
            return false;
        };

        const shouldZeroVariantPrice = (variantsCategory) =>
            Number(variantsCategory?.has_price) !== 1;

        let p = JSON.parse(JSON.stringify(product));
        p.variants_categories = [];
        for (const variants_category of product.variants_categories) {
            if (!isVariantCategoryEnabledForPos(variants_category)) continue;
            let vc = JSON.parse(JSON.stringify(variants_category));
            vc.min = Number.isFinite(Number(vc.min)) ? Number(vc.min) : 0;
            vc.max = Number.isFinite(Number(vc.max)) ? Number(vc.max) : 999;
            vc.force_mono = Number.isFinite(Number(vc.force_mono)) ? Number(vc.force_mono) : 0;
            vc.variants = [];
            vc.variants2 = [];
            vc.products = [];
            vc.products2 = [];
            for (const variant of variants_category.variants) {
                let v = JSON.parse(JSON.stringify(variant));
                if (shop && list.id) {
                    const priceList = variant.prices.filter((el) => {
                        return el.list_id == list.id && el.price !== null;
                    });
                    const assortment = variant.assortment.filter((el) => {
                        return el.shop_id == shop.id && el.list_id == list.id && el.active == 1;
                    });
                    if (priceList.length > 0 && assortment.length > 0) {
                        v.price = shouldZeroVariantPrice(variants_category) ? 0 : priceList[0].price;
                        vc.variants.push(v);
                    }
                } else {
                    if (shouldZeroVariantPrice(variants_category)) v.price = 0;
                    vc.variants.push(v);
                }
            }
            for (const variant of variants_category.variants2) {
                let v = JSON.parse(JSON.stringify(variant));
                if (shop && list.id) {
                    const priceList = variant.prices.filter((el) => {
                        return el.list_id == list.id && el.price !== null;
                    });
                    const assortment = variant.assortment.filter((el) => {
                        return el.shop_id == shop.id && el.list_id == list.id && el.active == 1;
                    });
                    if (priceList.length > 0 && assortment.length > 0) {
                        v.price = shouldZeroVariantPrice(variants_category) ? 0 : priceList[0].price;
                        vc.variants2.push(v);
                    }
                } else {
                    if (shouldZeroVariantPrice(variants_category)) v.price = 0;
                    vc.variants2.push(v);
                }
            }
            for (const variant of variants_category.products) {
                let v = JSON.parse(JSON.stringify(variant));
                if (shop && list.id) {
                    const priceList = variant.prices.filter((el) => {
                        return el.list_id == list.id && el.price !== null;
                    });
                    const assortment = variant.assortment.filter((el) => {
                        return el.shop_id == shop.id && el.list_id == list.id && el.active == 1;
                    });
                    if (
                        priceList.length > 0 &&
                        (assortment.length > 0) & check_ingredients_assortment(variant)
                    ) {
                        v.price = shouldZeroVariantPrice(variants_category) ? 0 : priceList[0].price;
                        vc.products.push(v);
                    }
                } else if (shop) {
                    if (check_ingredients_assortment(variant)) {
                        if (shouldZeroVariantPrice(variants_category)) v.price = 0;
                        vc.products.push(v);
                    }
                } else {
                    if (shouldZeroVariantPrice(variants_category)) v.price = 0;
                    vc.products.push(v);
                }
            }
            if (variants_category.products2) {
                for (const variant of variants_category.products2) {
                    let v = JSON.parse(JSON.stringify(variant));
                    if (shop && list.id) {
                        const priceList = variant.prices.filter((el) => {
                            return el.list_id == list.id && el.price !== null;
                        });
                        const assortment = variant.assortment.filter((el) => {
                            return el.shop_id == shop.id && el.list_id == list.id && el.active == 1;
                        });
                        if (
                            priceList.length > 0 &&
                            (assortment.length > 0) & check_ingredients_assortment(variant)
                        ) {
                            v.price = shouldZeroVariantPrice(variants_category) ? 0 : priceList[0].price;
                            vc.products2.push(v);
                        }
                    } else if (shop) {
                        if (check_ingredients_assortment(variant)) {
                            if (shouldZeroVariantPrice(variants_category)) v.price = 0;
                            vc.products2.push(v);
                        }
                    } else {
                        if (shouldZeroVariantPrice(variants_category)) v.price = 0;
                        vc.products2.push(v);
                    }
                }
            }
            if (
                vc.variants.length > 0 ||
                vc.variants2.length > 0 ||
                vc.products.length > 0 ||
                vc.products2.length > 0
            )
                p.variants_categories.push(vc);
        }
        return p;
    };

    const check_ingredients_assortment = (product) => {
        if (!apiSettings.menu_api) return true;
        if (!apiSettings.menu_api.enable_ingredients_assortment) return true;
        if (apiSettings.menu_api.enable_ingredients_assortment != 1) return true;
        if (!product.ingredients) return true;
        if (product.ingredients.length == 0) return true;
        for (const ingredient of product.ingredients) {
            const assortment = ingredient.assortment.filter((el) => {
                return el.shop_id == shop.id && el.active == 1;
            });
            if (assortment.length == 0) return false;
        }
        return true;
    };

    const getProductStock = (productId) => {
        if (stock && cart) {
            const s = stock.products.find((e) => e.shop_id == shop.id && e.product_id == productId);
            const i = stock.ingredients.filter((e) => e.shop_id == shop.id && e.product_id == productId);
            return {
                product: s ? parseInt(s.qty) : false,
                ingredients: i,
            };
        }
        return { product: false, ingredients: [] };
    };

    const getProductColor = (productId) => {
        const s = colors.find((e) => e.shop_id == shop.id && e.product_id == productId);
        if (s) return s.color;
        return false;
    };

    return (
        <div className="header">
            {cart && (
                <>
                    <ButtonLabeled
                        label=""
                        icon={<HamburgerIcon />}
                        vertical={true}
                        onClick={() => toggleSubmenu()}
                    />
                    <ButtonLabeled
                        label="Listino"
                        icon={<ListIcon />}
                        vertical={true}
                        value={list ? list.name : "Base"}
                        onClick={() =>
                            !lockEdit &&
                            setPopup({
                                title: "Listini",
                                content: (
                                    <List
                                        mode={3}
                                        elements={filteredLists}
                                        onClick={(l) => {
                                            setPopup(false);
                                            setList(l);
                                            if (cart.table)
                                                editTable(cart.table.id, {
                                                    list_id: l.id ? l.id : null,
                                                });
                                            editCart2({
                                                list_id: l.id,
                                            });
                                        }}
                                        cancelElement={
                                            apiSettings.pos.hide_base_list == 1 ? false : "Listino Base"
                                        }
                                    />
                                ),
                            })
                        }
                        disabled={lightMode}
                        onDisabledClick={openLightModeLockedPopup}
                        showInfoHint={lightMode}
                    />
                    <div className="separator" />
                    <ButtonLabeled
                        label="Banco"
                        selected={cart.delivery_type.id == "shop" && !cart.table}
                        icon={<BarIcon />}
                        vertical={true}
                        onClick={() => {
                            if (!lockEdit) {
                                editCart2({
                                    delivery_type: { id: "shop", name: "Sala" },
                                    delivery_date: null,
                                    address: false,
                                    delivery_time_range: null,
                                    room: null,
                                    table: null,
                                    self_cart_id: null,
                                });
                                setSearchTerm("");
                                setSearchAddressTerm("");
                                setActiveFlow(1);
                            }
                        }}
                    />
                    <ButtonLabeled
                        label="Tavolo"
                        selected={cart.delivery_type.id == "shop" && cart.table}
                        icon={<Table />}
                        vertical={true}
                        value={cart.table ? cart.table.name : ""}
                        onClick={() => !lockEdit && setTablesListVisible(true)}
                        disabled={lightMode}
                        onDisabledClick={openLightModeLockedPopup}
                        showInfoHint={lightMode}
                    />
                    {shop.takeaway == 1 && (
                        <ButtonLabeled
                            label="Asporto"
                            selected={cart.delivery_type.id == "takeaway"}
                            icon={<Takeaway />}
                            vertical={true}
                            onClick={() => {
                                if (!lockEdit) {
                                    editCart2({
                                        delivery_type: { id: "takeaway", name: "Asporto" },
                                        delivery_date: null,
                                        address: false,
                                        delivery_time_range: null,
                                        room: null,
                                        table: null,
                                        self_cart_id: null,
                                    });
                                    setSearchTerm("");
                                    setSearchAddressTerm("");
                                    setActiveFlow(1);
                                }
                            }}
                            disabled={lightMode}
                            onDisabledClick={openLightModeLockedPopup}
                            showInfoHint={lightMode}
                        />
                    )}
                    {shop.delivery == 1 && (
                        <ButtonLabeled
                            label="Domicilio"
                            selected={cart.delivery_type.id == "delivery"}
                            icon={<Delivery />}
                            vertical={true}
                            onClick={() => {
                                if (!lockEdit) {
                                    editCart2({
                                        delivery_type: { id: "delivery", name: "Domicilio" },
                                        delivery_date: null,
                                        address: false,
                                        delivery_time_range: null,
                                        table: null,
                                    });
                                    setSearchTerm("");
                                    setSearchAddressTerm("");
                                    setActiveFlow(1);
                                    setPopup({
                                        title: "Cliente",
                                        content: <CustomersPopUp />,
                                    });
                                }
                            }}
                            disabled={lightMode}
                            onDisabledClick={openLightModeLockedPopup}
                            showInfoHint={lightMode}
                        />
                    )}

                    <div className="separator" />
                    <ButtonLabeled
                        label="Cliente"
                        icon={<UserIcon />}
                        vertical={true}
                        valid={
                            (apiSettings.pos.mandatory_customer == 0 &&
                                cart.delivery_type.id != "delivery") ||
                            cart.origin == "glovo" ||
                            cart.origin == "deliveroo" ||
                            cart.origin == "justeat" ||
                            hasCustomerData
                        }
                        value={
                            cart.customer
                                ? cart.customer.surname + " " + cart.customer.name
                                : [cart.name, cart.surname].filter(Boolean).join(" ").trim()
                        }
                        subvalue={cart.address ? cart.address.address + " " + cart.address.number : ""}
                        onClick={() =>
                            !lockEdit &&
                            setPopup({
                                title: "Cliente",
                                content: <CustomersPopUp />,
                            })
                        }
                    />

                    {cart.delivery_type.id != "shop" && (
                        <>
                            <div className="separator" />
                            <ButtonLabeled
                                label="Data"
                                icon={<CalendarIcon />}
                                vertical={true}
                                valid={cart.delivery_date}
                                disabled={cart.delivery_type.id == "delivery" && !cart.address}
                                value={
                                    cart.delivery_date
                                        ? moment(cart.delivery_date, "YYYY-MM-DD HH:mm:ss").format(
                                            "DD/MM/YYYY HH:mm"
                                        )
                                        : ""
                                }
                                onClick={openDatePopup}
                            />
                        </>
                    )}

                    {apiSettings?.mandant?.reservations == 1 && (
                        <>
                            <div className="separator" />
                            <ButtonLabeled
                                label="Prenotazioni"
                                icon={<BookingTableIcon />}
                                vertical={true}
                                valid={true}
                                onClick={openReservationsClient}
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
}

export default Header;
